'use client';

/**
 * ClubesMapSplit — Layout 2 colunas pra view=mapa do /clubes (Figma 194:3943).
 *
 * Desktop (lg+): Esquerda lista (50%) | Direita mapa sticky top:112px (50%).
 * Mobile (< lg): Mapa em cima (h:70vh, bleed full-bleed via -mx-6/sm:-mx-10
 *   pra fugir do section padding e ocupar 100vw) | Lista embaixo (full width).
 *   mobile-tweaks-v2 lote 2 — Bruno 2026-05-21.
 *
 * Comportamento:
 *   - Click num card → seleciona pin no mapa (state=selected)
 *   - Click num pin → abre popup nativo do Mapbox com nome + link clicável
 *     pra página de detalhe do club. NÃO scrolla a lista (popup substitui).
 *   - Quando `clubs[]` cresce via paginação → fitBoundsToClubs(clubs)
 *     zoom-out animado pra englobar todos os novos pins
 *   - Botão "Carregar mais" no rodapé da lista (controlled externally)
 *
 * Sticky em desktop usa top:112px = header-mobile-padding (72) + 40 de respiro.
 */
import * as React from 'react';

import { Button } from '@brisa/ui/components/ui/button';
import { ClubCardFacelift } from '@brisa/ui/components/facelift/club-card/ClubCardFacelift';
import {
  ClubMapFacelift,
  type ClubMapFaceliftHandle,
  type ClubMapFaceliftItem,
} from '@brisa/ui/components/facelift/club-map/ClubMapFacelift';
import type { ClubListItem } from '@brisa/clubs';

/**
 * Fallback de coordenadas — usado quando um club existe na DB com `neighborhood`
 * preenchido mas SEM `location_lat`/`location_lng`. Em vez de sumir do mapa,
 * mostramos um pin no centro geográfico aproximado do bairro.
 *
 * Coords baseadas em centros visuais conhecidos de Barcelona (Wikipedia/Google).
 * Match case-insensitive + trim do nome do bairro pra tolerar variações leves.
 */
const NEIGHBORHOOD_CENTERS: Record<string, { lat: number; lng: number }> = {
  'gràcia': { lat: 41.403, lng: 2.1565 },
  'gracia': { lat: 41.403, lng: 2.1565 },
  'eixample': { lat: 41.3917, lng: 2.1649 },
  'el born': { lat: 41.3838, lng: 2.1816 },
  'born': { lat: 41.3838, lng: 2.1816 },
  'el raval': { lat: 41.3795, lng: 2.1697 },
  'raval': { lat: 41.3795, lng: 2.1697 },
  'poblenou': { lat: 41.4035, lng: 2.1968 },
  'sant antoni': { lat: 41.3795, lng: 2.1583 },
  'poble-sec': { lat: 41.3735, lng: 2.1626 },
  'poble sec': { lat: 41.3735, lng: 2.1626 },
  'barceloneta': { lat: 41.3796, lng: 2.1899 },
  'sants': { lat: 41.3727, lng: 2.1359 },
  'sarrià-sant gervasi': { lat: 41.4018, lng: 2.126 },
  'sarria-sant gervasi': { lat: 41.4018, lng: 2.126 },
};

function neighborhoodFallback(neighborhood: string | null | undefined): { lat: number; lng: number } | null {
  if (!neighborhood) return null;
  return NEIGHBORHOOD_CENTERS[neighborhood.trim().toLowerCase()] ?? null;
}

function toMapItem(c: ClubListItem): ClubMapFaceliftItem | null {
  const lat = c.location_lat ?? neighborhoodFallback(c.neighborhood)?.lat;
  const lng = c.location_lng ?? neighborhoodFallback(c.neighborhood)?.lng;
  if (lat == null || lng == null) return null;
  return {
    slug: c.slug,
    name: c.name,
    integration_status: c.integration_status === 'partner' ? 'partner' : 'listed',
    location_lat: lat,
    location_lng: lng,
  };
}

export interface ClubesMapSplitCopy {
  loadMore: string;
  loading: string;
}

export interface ClubesMapSplitProps {
  clubs: ClubListItem[];
  hasMore: boolean;
  isPending: boolean;
  onLoadMore: () => void;
  mapboxToken: string | undefined;
  locale: 'pt' | 'es';
  copy: ClubesMapSplitCopy;
  onMarkerClick?: (slug: string, status: 'partner' | 'listed') => void;
  /**
   * Ref opcional na coluna do mapa (outer wrapper). Permite que o parent
   * (ClubesView) observe a visibilidade do mapa via IntersectionObserver
   * pra decidir quando esconder a busca sticky no mobile.
   */
  mapColumnRef?: React.Ref<HTMLDivElement>;
}

export function ClubesMapSplit({
  clubs,
  hasMore,
  isPending,
  onLoadMore,
  mapboxToken,
  locale,
  copy,
  onMarkerClick,
  mapColumnRef,
}: ClubesMapSplitProps) {
  const mapRef = React.useRef<ClubMapFaceliftHandle>(null);
  const listRef = React.useRef<HTMLUListElement>(null);
  const [selectedSlug, setSelectedSlug] = React.useState<string | null>(null);
  const prevCountRef = React.useRef<number>(clubs.length);

  const mapItems = React.useMemo(
    () => clubs.map(toMapItem).filter((c): c is ClubMapFaceliftItem => c !== null),
    [clubs],
  );

  // Auto fitBounds quando clubs[] cresce (paginação trouxe clubs novos)
  React.useEffect(() => {
    if (clubs.length > prevCountRef.current && mapItems.length > 0) {
      mapRef.current?.fitBoundsToClubs(mapItems);
    }
    prevCountRef.current = clubs.length;
  }, [clubs.length, mapItems]);

  const handleMarkerClick = React.useCallback(
    (slug: string, status: 'partner' | 'listed') => {
      // Sync `selected` no card da lista (visual). NÃO scrolla lista —
      // popup nativo do Mapbox mostra nome + link clicável diretamente no mapa.
      setSelectedSlug(slug);
      onMarkerClick?.(slug, status);
    },
    [onMarkerClick],
  );

  const handleCardClick = React.useCallback((slug: string) => {
    setSelectedSlug(slug);
  }, []);

  return (
    <div
      data-slot="clubes-map-split"
      className="flex w-full flex-wrap gap-6"
    >
      {/* Coluna esquerda — lista de cards em 2 colunas internas.
          `shrink-0` é crítico: sem isso, flex items default têm
          `flex-shrink: 1` e podem encolher abaixo do width definido
          quando o irmão (mapa) pede mais espaço pelo conteúdo Mapbox. */}
      <div className="flex w-full shrink-0 flex-col gap-6 lg:w-[calc(50%-0.75rem)]">
        <ul
          ref={listRef}
          data-slot="clubes-map-split-list"
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
        >
          {clubs.map((club) => (
            <li
              key={club.slug}
              data-club-slug={club.slug}
              data-state={club.slug === selectedSlug ? 'selected' : 'default'}
              className="min-w-0 rounded-[32px] data-[state=selected]:ring-2 data-[state=selected]:ring-[hsl(var(--accent-yellow))]"
            >
              <ClubCardFacelift
                club={{
                  slug: club.slug,
                  name: club.name,
                  neighborhood: club.neighborhood,
                  cover_url: club.photos?.[0]?.url ?? null,
                  avatar_url: club.photos?.[0]?.url ?? null,
                  integration_status: club.integration_status,
                  vibe: club.vibe?.[0],
                }}
                href={`/clubes/${club.slug}`}
                dropShadow={false}
                onClick={() => handleCardClick(club.slug)}
                data-event="club-card-clicked"
                data-club-slug={club.slug}
              />
            </li>
          ))}
        </ul>

        {hasMore && (
          <div className="flex justify-center">
            <Button
              type="button"
              variant="secondary-pill"
              size="md"
              onClick={onLoadMore}
              disabled={isPending}
              aria-busy={isPending || undefined}
              data-event="clubes-load-more"
            >
              {isPending ? copy.loading : copy.loadMore}
            </Button>
          </div>
        )}
      </div>

      {/* Coluna direita — mapa.
          Mobile (< lg): order-first (mapa em cima) + altura 70vh + full-bleed
          via -mx-6/sm:-mx-10 (escapa do section padding pra ocupar 100vw).
          Desktop (lg+): order normal (mapa à direita) + sticky top:112px com
          altura calc(100vh - header - bottom-pad). */}
      <div
        ref={mapColumnRef}
        data-slot="clubes-map-split-map"
        className="relative order-first w-full shrink-0 lg:order-none lg:w-[calc(50%-0.75rem)]"
      >
        <div className="relative -mx-6 h-[70vh] sm:-mx-10 lg:mx-0 lg:sticky lg:top-[112px] lg:h-[calc(100vh-136px)] lg:min-h-[480px]">
          <ClubMapFacelift
            ref={mapRef}
            clubs={mapItems}
            locale={locale}
            mapboxToken={mapboxToken}
            selectedClubSlug={selectedSlug}
            onMarkerClick={handleMarkerClick}
            // mobile: sem rounded no topo (lg: restaura 24px nos 4 cantos
            // via desktop). `!` força !important pra vencer borderRadius
            // inline 24 que vem do ClubMapFacelift.
            className="!rounded-t-none lg:!rounded-[24px]"
          />
        </div>
      </div>
    </div>
  );
}
